import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { load } from '@cashfreepayments/cashfree-js';
import { CASHFREE_MODE } from '../../config/environment';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Grid,
  Divider,
  Paper,
  Skeleton,
  Chip,
  CircularProgress
} from '@mui/material';
import { Delete, Lock, CalendarToday, ArrowForward } from '@mui/icons-material';
import { ColorsConstants, dashboardCardBgColor, grayFontColor } from '../../cssUtils/constants.js';
import { POST_CREATE_SUBSCRIPTION, POST_CALCULATE_CHECKOUT } from '../../config/urls.js';
import {
  denormalizeCycle,
  normalizeCycle,
  formatPrice as formatPriceUtil,
  getAddonDisplayName,
} from './utils.js';
import ButtonTwo from '../../common/dashboard-button.jsx';
import BackButton from '../../common/back-button.jsx';

const CheckoutSection = ({
  selectedPlan,
  selectedBillingCycle = 'quarterly',
  selectedAddons = [],
  billingCurrency = 'INR',
  currentSubscription = null,
  onRemoveAddon,
  onAddMoreAddons,
  isMutualFundPlan = false,
  loading = false,
  checkoutPage,
  setCheckoutPage
}) => {
  // Onboarding fee applies only for trial tenants and expired-trial tenants.
  // Active-plan renewals (status "active", "past_due", "suspended") are exempt.
  const subscriptionStatus = currentSubscription?.status?.toLowerCase();
  const shouldApplyOnboardingFee =
    !currentSubscription ||
    subscriptionStatus === 'trial' ||
    subscriptionStatus === 'expired';
  // State to handle payment processing spinner
  const [isProcessing, setIsProcessing] = useState(false);

  // Backend-calculated costs (replaces all client-side maths)
  const [backendCosts, setBackendCosts] = useState(null);
  const [loadingCosts, setLoadingCosts] = useState(false);

  // Initialize SDK Ref
  const cashfreeRef = useRef(null);

  // Load Cashfree SDK once on mount
  useEffect(() => {
    const initializeSDK = async () => {
      if (typeof window === "undefined") return;
      try {
        const cf = await load({ mode: CASHFREE_MODE });
        if (!cf) {
          console.error("❌ SDK load() returned null");
          return;
        }
        console.log("✅ SDK loaded successfully");
        cashfreeRef.current = cf;
      } catch (e) {
        console.error("❌ Failed to load SDK:", e);
      }
    };
    initializeSDK();
  }, []);

  // Re-calculate pricing from backend whenever plan / addons / cycle changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchBreakdown = async () => {
      if (!selectedPlan?.id || !selectedBillingCycle) return;

      setLoadingCosts(true);
      try {
        const billableAddons = selectedAddons.filter((a) => a.addon_type !== 'one_time');
        const standardAddonIds = billableAddons.map((a) => a.id);
        const addonConfigs = billableAddons
          .filter((a) => a.quantity !== undefined && a.quantity > 0)
          .map((a) => ({ addon_id: a.id, quantity: a.quantity }));

        const payload = {
          plan_id: selectedPlan.id,
          billing_cycle: denormalizeCycle(selectedBillingCycle?.toLowerCase()),
          addon_ids: [...standardAddonIds],
          ...(addonConfigs.length > 0 && { addon_configs: addonConfigs }),
          apply_onboarding_fee: shouldApplyOnboardingFee,
        };

        const response = await axios.post(POST_CALCULATE_CHECKOUT, payload, {
          headers: { 'X-Tenant-ID': localStorage.getItem('tenant') },
        });
        setBackendCosts(response.data);
      } catch (err) {
        console.error("❌ Failed to fetch checkout breakdown:", err);
        setBackendCosts(null);
      } finally {
        setLoadingCosts(false);
      }
    };

    fetchBreakdown();
  // JSON.stringify(selectedAddons) ensures re-fetch when quantity / items change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?.id, selectedBillingCycle, JSON.stringify(selectedAddons), shouldApplyOnboardingFee]);

  // Bind billingCurrency so call sites don't need to pass it each time
  const formatPrice = (price) => formatPriceUtil(price, billingCurrency);

  // Get addon price based on the selected billing cycle (Supports both UI flows)
  const getAddonPrice = (addon) => {
    if (!addon) return 0;

    // 1. Configurable addon (e.g. additional_agent) — quantity was set via modal.
    //    The addon carries pricePerUnit and quantity injected by handleConfigConfirm.
    //    Always honour these so the full quantity-multiplied amount is used.
    if (addon.quantity !== undefined && addon.pricePerUnit !== undefined) {
      return parseFloat(addon.pricePerUnit) * addon.quantity;
    }

    // 2. Check the Pricing Array (Banner Flow / Raw Backend Data)
    if (addon.pricing && Array.isArray(addon.pricing)) {
      const normalizedCycle = normalizeCycle(selectedBillingCycle);
      const activePricing = addon.pricing.find(
        (p) => normalizeCycle(p.billing_cycle) === normalizedCycle && p.is_active
      );

      if (activePricing) {
        return parseFloat(activePricing.price);
      }
    }

    // 3. Fallback to flat price (Normal Flow / UI Injected Data)
    if (addon.price !== undefined) {
      return parseFloat(addon.price);
    }

    // 4. Absolute Fallback
    return 0;
  };

  // Get billing period display
  const getBillingPeriod = () => {
    const cycle = normalizeCycle(selectedBillingCycle);
    if (cycle === 'annual') return 'year';
    if (cycle === 'quarterly') return 'quarter';
    return 'month';
  };

  // Get plan display name
  const getPlanDisplayName = () => {
    if (!selectedPlan?.name) return 'No plan selected';
    return selectedPlan.name.replace(/_/g, ' ');
  };

  // Normalise backend response into the shape used by JSX below.
  // Falls back to zeros while the request is in-flight or if it fails.
  const costs = backendCosts
    ? {
        planCost:             backendCosts.plan_cost,
        preDiscountPlanCost:  backendCosts.pre_discount_plan_cost ?? backendCosts.plan_cost,
        discountAmount:       backendCosts.discount_amount ?? 0,
        addonsCost:           backendCosts.addon_cost ?? backendCosts.addons_cost,
        addonBreakdown:       (backendCosts.addon_breakdown || []),
        subtotal:             backendCosts.subtotal,
        taxBreakdown:         (backendCosts.tax_breakdown || []).map((t) => ({
          name:        t.name,
          rateDisplay: t.rate_display,
          amount:      t.amount,
        })),
        totalTax: backendCosts.total_tax,
        total:    backendCosts.total,
        perMonth: backendCosts.per_month,
      }
    : { planCost: 0, preDiscountPlanCost: 0, discountAmount: 0, addonsCost: 0, addonBreakdown: [], subtotal: 0, taxBreakdown: [], totalTax: 0, total: 0, perMonth: 0 };

  // One-time onboarding fee auto-included by backend (not in selectedAddons)
  const onboardingAddon = costs.addonBreakdown.find((b) => b.name === 'onboarding_fee');
  const onboardingAddonCost = onboardingAddon
    ? (onboardingAddon.total_price ?? onboardingAddon.total_cost ?? onboardingAddon.cost ?? 0)
    : 0;
  const hasOneTimeFee = shouldApplyOnboardingFee && onboardingAddonCost > 0;

  // Calculate next billing date
  const getNextBillingDate = () => {
    const today = new Date();
    const nextDate = new Date(today);
    const cycle = normalizeCycle(selectedBillingCycle);

    if (cycle === 'annual') {
      nextDate.setFullYear(today.getFullYear() + 1);
    } else if (cycle === 'quarterly') {
      nextDate.setMonth(today.getMonth() + 3);
    } else {
      nextDate.setMonth(today.getMonth() + 1);
    }

    return nextDate.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Handle Payment Logic
  // Handle Payment Logic
  const handlePayment = async () => {
    if (!cashfreeRef.current) {
        alert("Payment SDK not loaded yet. Please wait a moment.");
        return;
    }

    setIsProcessing(true);
    try {
      const billableAddons = selectedAddons.filter(addon => addon.addon_type !== 'one_time');
      const standardAddonIds = billableAddons.map(addon => addon.id);
      const addonConfigs = billableAddons
        .filter(addon => addon.quantity !== undefined && addon.quantity > 0)
        .map(addon => ({ addon_id: addon.id, quantity: addon.quantity }));

      const payload = {
        plan_id: selectedPlan.id,
        billing_cycle: denormalizeCycle(selectedBillingCycle?.toLowerCase()),
        addon_ids: [...standardAddonIds],
        ...(addonConfigs.length > 0 && { addon_configs: addonConfigs }),
        apply_onboarding_fee: shouldApplyOnboardingFee,
      };

      console.log("🚀 Sending Payload:", payload);

      const response = await axios.post(POST_CREATE_SUBSCRIPTION, payload, {
          headers: { 'X-Tenant-ID': localStorage.getItem('tenant') }
      });

      console.log("📨 Backend Response Data:", response.data);

      // 🟢 Look for payment_session_id first, fallback to subscription_session_id just in case
      const sessionId = response.data.payment_session_id || response.data.subscription_session_id;

      if (sessionId && cashfreeRef.current) {
        console.log(`🎫 Starting Checkout (${backendCosts?.is_subscription ? 'Subscription' : 'Direct Order'}) with Session ID:`, sessionId);
        
        // Route to the correct SDK method based on the amount
        if (backendCosts?.is_subscription) {
            // Autopilot Subscription
            cashfreeRef.current.subscriptionsCheckout({
              subsSessionId: sessionId,
              redirectTarget: "_self",
            });
        } else {
            // Standard Direct Order
            cashfreeRef.current.checkout({
              paymentSessionId: sessionId,
              redirectTarget: "_self",
            });
        }

      } else {
        console.error("❌ Session ID missing from response:", response.data);
        alert("Error: Server did not return a valid session ID.");
      }

    } catch (error) {
      console.error("❌ Payment Initialization Failed:", error);
      alert("Failed to start payment. Please try again.");
    } finally {
        setIsProcessing(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Skeleton variant="rounded" height={200} sx={{ mb: 3 }} />
          <Skeleton variant="rounded" height={300} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Skeleton variant="rounded" height={500} />
        </Grid>
      </Grid>
    );
  }

  // No plan selected
  if (!selectedPlan) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          No plan selected
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Please select a plan from the Plans & Pricing tab
        </Typography>
        <Button
          variant="contained"
          onClick={() => onAddMoreAddons?.() || (() => {})}
          sx={{
            backgroundColor: ColorsConstants.primaryPurple100,
            fontWeight: 'bold',
          }}
        >
          Go to Plans
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <BackButton onClick={onAddMoreAddons}/>
      </Box>
      <Grid container spacing={3}>
      {/* Left Column - Plan & Add-ons */}
      <Grid item xs={12} md={8}>
        {/* Selected Plan */}
        <Card
          sx={{
            mb: 3,
            border: `1px solid ${ColorsConstants.primaryGray10}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: ColorsConstants.primaryBackgroundColor,
                  mb: 3,
                }}
              >
                Selected Plan
              </Typography>
            </Box>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: `2px solid ${ColorsConstants.primaryPurple100}`,
                backgroundColor: dashboardCardBgColor,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: ColorsConstants.primaryBackgroundColor,
                      mb: 0.5,
                      textTransform: 'capitalize',
                    }}
                  >
                    {getPlanDisplayName()} Plan
                  </Typography>
                  <Typography variant="body2" sx={{ color: grayFontColor }}>
                    {selectedPlan?.description || 'Comprehensive messaging solution'}
                  </Typography>
                  <Chip
                    label={normalizeCycle(selectedBillingCycle) === 'annual' ? 'Annual' : normalizeCycle(selectedBillingCycle) === 'quarterly' ? 'Quarterly' : 'Monthly'}
                    size="small"
                    color="primary"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 'bold',
                      color: ColorsConstants.primaryBackgroundColor,
                    }}
                  >
                    {formatPrice(costs.planCost)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: grayFontColor }}>
                    /{getBillingPeriod()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Add-ons card — user-selectable parts hidden for MF plans, auto-included always shown */}
        <Card
          sx={{
            border: `1px solid ${ColorsConstants.primaryGray10}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: ColorsConstants.primaryBackgroundColor,
                }}
              >
                Selected Add-ons
              </Typography>
              {!isMutualFundPlan && selectedAddons.length > 0 && (
                <Typography variant="body2" sx={{ color: grayFontColor }}>
                  {selectedAddons.length} selected
                </Typography>
              )}
            </Box>

            {!isMutualFundPlan && (
              selectedAddons.length === 0 ? (
                // Only show the empty state when we're certain there are no auto-included addons
                !loadingCosts && costs.addonBreakdown.filter((b) => !selectedAddons.find((a) => a.id === b.addon_id)).length === 0 && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: grayFontColor,
                      textAlign: 'center',
                      py: 5,
                    }}
                  >
                    No add-ons selected
                  </Typography>
                )
              ) : (
                <Box>
                  {selectedAddons.map((addon, index) => {
                    const addonPrice = getAddonPrice(addon);
                    const backendAddon = costs.addonBreakdown.find((b) => b.addon_id === addon.id);
                    const displayCost = backendAddon
                      ? (backendAddon.total_cost ?? backendAddon.cost ?? addonPrice)
                      : addonPrice;

                    return (
                      <Box
                        key={addon.id || index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          mb: 1,
                          border: `1px solid ${ColorsConstants.primaryGray10}`,
                          borderRadius: 1,
                          backgroundColor: 'white',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: ColorsConstants.primaryBackgroundColor,
                            }}
                          >
                            {getAddonDisplayName(addon)}
                          </Typography>
                          {/* Prefer backend breakdown detail; fall back to client-side quantity info */}
                          {backendAddon && backendAddon.unit_price != null ? (
                            <Typography
                              variant="caption"
                              sx={{ color: grayFontColor, display: 'block' }}
                            >
                              {backendAddon.quantity > 1
                                ? `${backendAddon.quantity} units × ${formatPrice(backendAddon.unit_price)}`
                                : formatPrice(backendAddon.unit_price)}
                            </Typography>
                          ) : (
                            addon.quantity > 0 && addon.pricePerUnit !== undefined && (
                              <Typography
                                variant="caption"
                                sx={{ color: grayFontColor, display: 'block' }}
                              >
                                {addon.quantity} {addon.quantity === 1 ? 'agent' : 'agents'} × {formatPrice(addon.pricePerUnit)}
                              </Typography>
                            )
                          )}
                          <Typography
                            variant="caption"
                            sx={{ color: grayFontColor }}
                          >
                            {addon?.description || 'Additional feature'}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            color: ColorsConstants.primaryBackgroundColor,
                            mr: 2,
                          }}
                        >
                          {loadingCosts ? <Skeleton width={70} /> : formatPrice(displayCost)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => onRemoveAddon(addon.id)}
                          sx={{
                            backgroundColor: '#fc8181',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#f56565',
                            },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )
            )}

            {/* Auto-included add-ons from backend (e.g. onboarding fee) — shown for all plan types */}
            {!loadingCosts && costs.addonBreakdown
              .filter((b) => !selectedAddons.find((a) => a.id === b.addon_id))
              .map((b, i) => (
                <Box key={`auto-${i}`} sx={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', p: 2, mb: 1,
                  border: `1px solid ${ColorsConstants.primaryGray10}`,
                  borderRadius: 1, backgroundColor: 'white' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1"
                      sx={{ fontWeight: 600, color: ColorsConstants.primaryBackgroundColor }}>
                      {getAddonDisplayName({ name: b.name })}
                    </Typography>
                    {b.unit_price != null && b.quantity > 1 && (
                      <Typography variant="caption" sx={{ color: grayFontColor, display: 'block' }}>
                        {b.quantity} units × {formatPrice(b.unit_price)}
                      </Typography>
                    )}
                    <Chip label="Included" size="small"
                      sx={{ fontSize: '0.65rem', height: 18, mt: 0.5,
                        backgroundColor: '#EDE9FE', color: '#5B21B6' }} />
                  </Box>
                  <Typography variant="h6"
                    sx={{ fontWeight: 'bold', color: ColorsConstants.primaryBackgroundColor, mr: 2 }}>
                    {formatPrice(b.total_price ?? b.total_cost ?? b.cost)}
                  </Typography>
                </Box>
              ))
            }

            {!isMutualFundPlan && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={onAddMoreAddons}
                  sx={{
                    borderColor: ColorsConstants.primaryPurple100,
                    color: ColorsConstants.primaryPurple100,
                    fontWeight: 600,
                  }}
                >
                  + Add More Add-ons
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Right Column - Order Summary */}
      <Grid item xs={12} md={4}>
        <Card
          sx={{
            position: 'sticky',
            top: 100,
            border: `1px solid ${ColorsConstants.primaryGray10}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: ColorsConstants.primaryBackgroundColor,
                mb: 3,
              }}
            >
              Order Summary
            </Typography>

            {/* Billing Info */}
            <Paper
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: dashboardCardBgColor,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: ColorsConstants.primaryGray70 }}>
                <CalendarToday sx={{ fontSize: 16, verticalAlign: 'middle', mr: 1 }} />
                Billing Details
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: grayFontColor }}>
                    Billing Cycle
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {normalizeCycle(selectedBillingCycle) === 'annual' ? 'Annual' : normalizeCycle(selectedBillingCycle) === 'quarterly' ? 'Quarterly' : 'Monthly'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2" sx={{ color: grayFontColor }}>
                    Next Billing
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {getNextBillingDate()}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }} />

            {/* Cost Breakdown */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: grayFontColor }}
                >
                  Plan Cost
                </Typography>
                {loadingCosts
                  ? <Skeleton width={60} />
                  : <Box sx={{ textAlign: 'right' }}>
                      {costs.discountAmount > 0 && (
                        <Typography variant="body2" sx={{ color: '#94a3b8', textDecoration: 'line-through', fontSize: '0.78rem' }}>
                          {formatPrice(costs.preDiscountPlanCost)}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatPrice(costs.planCost)}
                      </Typography>
                    </Box>}
              </Box>

              {costs.discountAmount > 0 && !loadingCosts && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#16a34a' }}>
                    Promo Discount
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#16a34a' }}>
                    – {formatPrice(costs.discountAmount)}
                  </Typography>
                </Box>
              )}

              {(selectedAddons.length > 0 || costs.addonBreakdown.length > 0) && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>
                      Add-ons ({costs.addonBreakdown.length || selectedAddons.length})
                    </Typography>
                    {loadingCosts
                      ? <Skeleton width={60} />
                      : <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatPrice(costs.addonsCost)}
                        </Typography>}
                  </Box>
                  {/* Per-addon breakdown from backend (covers both selected and auto-included addons) */}
                  {!loadingCosts && costs.addonBreakdown.length > 0 && costs.addonBreakdown.map((b, i) => {
                    const addonObj = selectedAddons.find((a) => a.id === b.addon_id);
                    const lineCost = b.total_price ?? b.total_cost ?? b.cost;
                    return (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, pl: 1 }}>
                        <Typography variant="body2" sx={{ color: grayFontColor, fontSize: '0.78rem' }}>
                          {getAddonDisplayName(addonObj || { name: b.name })}
                          {b.quantity > 1 ? ` ×${b.quantity}` : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                          {formatPrice(lineCost)}
                        </Typography>
                      </Box>
                    );
                  })}
                  {loadingCosts && <Skeleton height={20} width="100%" sx={{ mb: 0.5 }} />}
                </>
              )}
              
              <Paper
                sx={{
                  p: 1.5,
                  backgroundColor: dashboardCardBgColor,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: ColorsConstants.primaryGray70 }}
                  >
                    Subtotal
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatPrice(costs.subtotal)}
                  </Typography>
                </Box>
              </Paper>
              
              {/* Tax Breakdown — sourced from backend */}
              {loadingCosts ? (
                <Box sx={{ my: 1 }}>
                  <Skeleton height={20} width="100%" />
                  <Skeleton height={20} width="80%" />
                </Box>
              ) : (
                costs.taxBreakdown.map((tax, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mb: 0.5,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: grayFontColor }}>
                        {tax.name} ({tax.rateDisplay}%)
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatPrice(tax.amount)}
                        </Typography>
                    </Box>
                ))
              )}

            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Total */}
            <Box sx={{ mb: 3 }}>
              {/* Recurring vs one-time split — shown only when an onboarding fee is present */}
              {!loadingCosts && hasOneTimeFee && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>
                      Recurring ({getBillingPeriod()}ly)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatPrice(costs.total - onboardingAddonCost)}/{getBillingPeriod()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: grayFontColor }}>
                      One-time Onboarding Fee
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatPrice(onboardingAddonCost)}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                </>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: ColorsConstants.primaryBackgroundColor,
                  }}
                >
                  {hasOneTimeFee ? 'Amount Due Today' : 'Total Amount'}
                </Typography>
                {loadingCosts
                  ? <Skeleton width={80} height={48} />
                  : (
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 'bold', color: ColorsConstants.primaryPurple100 }}
                    >
                      {formatPrice(costs.total)}
                    </Typography>
                  )}
              </Box>
              <Typography
                variant="caption"
                sx={{ color: grayFontColor, display: 'block', textAlign: 'right' }}
              >
                {loadingCosts
                  ? <Skeleton width={100} />
                  : hasOneTimeFee
                    ? `Then ${formatPrice(costs.total - onboardingAddonCost)}/${getBillingPeriod()} from ${getNextBillingDate()}`
                    : `(${formatPrice(costs.total)}/${getBillingPeriod()})`}
              </Typography>
            </Box>

            {/* Payment Button with Loading State */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handlePayment}
              disabled={!selectedPlan || isProcessing}
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <Lock />}
              endIcon={!isProcessing && <ArrowForward />}
              sx={{
                py: 1.5,
                fontWeight: 'bold',
                fontSize: '1.1rem',
                backgroundColor: ColorsConstants.primaryPurple100,
                '&:hover': {
                  backgroundColor: ColorsConstants.primaryBackgroundColor,
                },
                '&.Mui-disabled': {
                  backgroundColor: ColorsConstants.primaryGray20,
                },
              }}
            >
              {isProcessing ? "Processing..." : "Proceed to Payment"}
            </Button>

            {/* Security Badge */}
            <Paper
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: dashboardCardBgColor,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: grayFontColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 1,
                }}
              >
                <Lock fontSize="small" />
                Secure payment powered by Cashfree
              </Typography>
              <Typography variant="caption" sx={{ color: grayFontColor }}>
                Plan activates immediately after payment
              </Typography>
            </Paper>

            {/* Pro Tip */}
            {normalizeCycle(selectedBillingCycle) !== 'annual' && (
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#fef5e7',
                  borderLeft: `4px solid #f39c12`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#975a16',
                    fontWeight: 'bold',
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  💡 Pro Tip:
                </Typography>
                <Typography variant="caption" sx={{ color: '#975a16' }}>
                  Switch to Annual billing and save 2 months!
                </Typography>
              </Paper>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    </Box>
  );
};

export default CheckoutSection;
